import React, { useCallback, useEffect, useState } from 'react'
import { Card, Col, Row, Statistic, Table, Tabs, Tag, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { get } from '../../lib/request'
import {
  LEAD_STATUS_LABELS,
  LeadStatus,
  PageData,
  SOURCE_MODE_LABELS,
  errMsg,
  fmtMoney,
} from '../../lib/api'

const { Text } = Typography

interface EffectReport {
  totalLeads: number
  dealtLeads: number
  conversionRate: number
  byStatus: Array<{ status: LeadStatus; count: number }>
  bySource: Array<{ sourceMode: string; count: number }>
  reward: { count: number; totalAmount: number | string | null }
}

interface PerfRow {
  id: number
  name: string
  rid: string
  referrerType: string
  tags?: string | null
  totalLeads: number
  dealtLeads: number
  rewardTotal: number | string | null
  conversionRate: number
}

/** 未核销券报表分组（GET /admin/reports/coupon-outstanding，按 totalCouponAmount 降序） */
interface CouponGroup {
  merchantId: number | null
  merchantName: string
  count: number
  totalCouponAmount: number
  items: Array<{
    rewardId: number
    referrerName: string
    rid: string
    couponAmount: number | null
    settlementStatus: string | null
  }>
}

const BAR_COLORS = ['#2557A7', '#16A085', '#F39C12', '#C9A84C', '#8E44AD', '#E74C3C', '#7F8C8D']

/** 轻量水平条形图（无图表库依赖） */
const MiniBars: React.FC<{ rows: Array<{ label: string; count: number }> }> = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <div>
      {rows.map((r, i) => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ width: 110, fontSize: 13, textAlign: 'right', flexShrink: 0 }}>{r.label}</span>
          <div style={{ flex: 1, background: '#F0F2F5', borderRadius: 4, height: 18, overflow: 'hidden' }}>
            <div
              style={{
                width: `${(r.count / max) * 100}%`,
                height: '100%',
                background: BAR_COLORS[i % BAR_COLORS.length],
              }}
            />
          </div>
          <span style={{ width: 44, fontSize: 13, fontWeight: 600 }}>{r.count}</span>
        </div>
      ))}
    </div>
  )
}

/** 报表：推荐效果总报表 + 推荐人绩效排行 */
const ReportsPage: React.FC = () => {
  const [effect, setEffect] = useState<EffectReport | null>(null)
  const [effectLoading, setEffectLoading] = useState(true)

  const [perf, setPerf] = useState<PerfRow[]>([])
  const [perfTotal, setPerfTotal] = useState(0)
  const [perfPage, setPerfPage] = useState(1)
  const [perfLoading, setPerfLoading] = useState(false)

  // thankyou_v2 收尾：未核销券 Tab（与 effect 同生命期，首屏即拉）
  const [couponGroups, setCouponGroups] = useState<CouponGroup[]>([])
  const [couponLoading, setCouponLoading] = useState(false)

  useEffect(() => {
    setCouponLoading(true)
    get<CouponGroup[]>('/admin/reports/coupon-outstanding')
      .then((d) => setCouponGroups(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '未核销券报表加载失败')))
      .finally(() => setCouponLoading(false))
  }, [])

  useEffect(() => {
    get<EffectReport>('/admin/reports/effect')
      .then(setEffect)
      .catch((err) => message.warning(errMsg(err, '效果报表加载失败')))
      .finally(() => setEffectLoading(false))
  }, [])

  const fetchPerf = useCallback(() => {
    setPerfLoading(true)
    get<PageData<PerfRow>>('/admin/reports/referrer-performance', {
      params: { page: perfPage, pageSize: 20 },
    })
      .then((d) => {
        setPerf(d.items ?? [])
        setPerfTotal(d.total ?? 0)
      })
      .catch((err) => message.warning(errMsg(err, '绩效报表加载失败')))
      .finally(() => setPerfLoading(false))
  }, [perfPage])

  useEffect(() => { fetchPerf() }, [fetchPerf])

  const perfColumns: ColumnsType<PerfRow> = [
    {
      title: '排名',
      key: 'rank',
      width: 70,
      render: (_: unknown, __: PerfRow, idx: number) => `#${(perfPage - 1) * 20 + idx + 1}`,
    },
    { title: '推荐人', dataIndex: 'name', key: 'name' },
    { title: '推荐码', dataIndex: 'rid', key: 'rid' },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      render: (v?: string | null) =>
        v ? v.split(',').filter(Boolean).map((t) => <Tag key={t}>{t.trim()}</Tag>) : '—',
    },
    { title: '总线索', dataIndex: 'totalLeads', key: 'totalLeads' },
    { title: '成交数', dataIndex: 'dealtLeads', key: 'dealtLeads' },
    {
      title: '转化率',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      render: (v: number) => `${(v * 100).toFixed(1)}%`,
    },
    {
      title: '累计奖励金额',
      dataIndex: 'rewardTotal',
      key: 'rewardTotal',
      render: (v: number | string | null) => fmtMoney(v),
    },
  ]

  // thankyou_v2 收尾：未核销券明细子表列
  const couponItemColumns: ColumnsType<CouponGroup['items'][number]> = [
    { title: '答谢单号', dataIndex: 'rewardId', key: 'rewardId', render: (v: number) => `#R-${v}` },
    { title: '推荐人', dataIndex: 'referrerName', key: 'referrerName' },
    { title: '推荐码', dataIndex: 'rid', key: 'rid' },
    {
      title: '券面额',
      dataIndex: 'couponAmount',
      key: 'couponAmount',
      render: (v: number | null) => fmtMoney(v),
    },
    { title: '结算状态', dataIndex: 'settlementStatus', key: 'settlementStatus', render: (v: string | null) => v ?? '—' },
  ]

  const couponColumns: ColumnsType<CouponGroup> = [
    { title: '商家', dataIndex: 'merchantName', key: 'merchantName' },
    { title: '笔数', dataIndex: 'count', key: 'count', width: 100 },
    {
      title: '未兑付券总额',
      dataIndex: 'totalCouponAmount',
      key: 'totalCouponAmount',
      render: (v: number) => fmtMoney(v),
    },
  ]

  return (
    <Tabs
      items={[
        {
          key: 'effect',
          label: '推荐效果总报表',
          children: (
            <>
              <Row gutter={[16, 16]}>
                {[
                  { label: '累计线索', value: effect?.totalLeads },
                  { label: '累计成交', value: effect?.dealtLeads, color: '#27AE60' },
                  {
                    label: '整体转化率',
                    value: effect ? `${(effect.conversionRate * 100).toFixed(1)}%` : undefined,
                    color: '#2557A7',
                  },
                  {
                    label: `奖励发放（${effect?.reward.count ?? 0} 笔）`,
                    value: effect ? fmtMoney(effect.reward.totalAmount) : undefined,
                    color: '#C9A84C',
                  },
                ].map((c) => (
                  <Col key={c.label} xs={12} md={6}>
                    <Card style={{ borderRadius: 12 }} loading={effectLoading}>
                      <Statistic
                        title={c.label}
                        value={c.value ?? '—'}
                        valueStyle={{ color: c.color ?? '#2C3E50', fontWeight: 700 }}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={12}>
                  <Card title="按状态分布" style={{ borderRadius: 12 }} loading={effectLoading}>
                    <MiniBars
                      rows={(effect?.byStatus ?? []).map((s) => ({
                        label: LEAD_STATUS_LABELS[s.status]?.label ?? s.status,
                        count: s.count,
                      }))}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="按来源分布" style={{ borderRadius: 12 }} loading={effectLoading}>
                    <MiniBars
                      rows={(effect?.bySource ?? []).map((s) => ({
                        label: SOURCE_MODE_LABELS[s.sourceMode] ?? s.sourceMode,
                        count: s.count,
                      }))}
                    />
                  </Card>
                </Col>
              </Row>
              <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                明细趋势（按日/周/月）见「工作台」线索趋势图；本页为累计口径汇总。
              </Text>
            </>
          ),
        },
        {
          key: 'performance',
          label: '推荐人绩效',
          children: (
            <Table<PerfRow>
              rowKey="id"
              loading={perfLoading}
              columns={perfColumns}
              dataSource={perf}
              scroll={{ x: 'max-content' }}
              style={{ background: '#fff', borderRadius: 12 }}
              pagination={{
                current: perfPage, pageSize: 20, total: perfTotal,
                showTotal: (t) => `共 ${t} 条`, onChange: (p) => setPerfPage(p),
              }}
            />
          ),
        },
        {
          key: 'coupon',
          label: '未核销券',
          children: (
            <Card title="代金券未兑付汇总（按商家）" style={{ borderRadius: 12 }}>
              <Table<CouponGroup>
                rowKey={(g) => String(g.merchantId ?? 'none')}
                loading={couponLoading}
                columns={couponColumns}
                dataSource={couponGroups}
                pagination={false}
                expandable={{
                  expandedRowRender: (g) => (
                    <Table<CouponGroup['items'][number]>
                      rowKey="rewardId"
                      columns={couponItemColumns}
                      dataSource={g.items}
                      pagination={false}
                      size="small"
                    />
                  ),
                }}
              />
            </Card>
          ),
        },
      ]}
    />
  )
}

export default ReportsPage
